import type { FC, KeyboardEventHandler } from "react"
import React from "react"

import { mapObject } from "@logic/lib/utils/mapObject"
import { GlobalHotKeys } from "react-hotkeys"
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil"

import { useToggleState } from "~/hooks/recoil"

import { nodeIndex } from "./state/elements"
import { panelSelectionRotationState } from "./state/elements/panels/rotating"
import { useUndoRedo } from "./state/elements/timeline/transactions"
import {
  useDeleteSelection,
  useSelectAll,
  useClearSelection,
} from "./state/elements/transactions"
import { useSwapPreviewEdgeAngle } from "./state/elements/transactions/previewEdgeLineLocking"
import { shiftIsDownState } from "./state/inputs"
import {
  temporaryToolState,
  toolState,
  TOOL_HOTKEYS,
  useTrySwapToPanelEraser,
} from "./state/tool"
import {
  edgeDesignationState,
  EDGE_DESIGNATOR_HOTKEYS,
} from "./state/tool/edgeDesignation"
import { debugPanelIsPresentState } from "./Studio/Debug/state"
import type { UseDesign } from "../Proposal/state/designs/use-design"
import { currentUserIsDesignRequestUserState } from "../Proposal/state/user/user-state"

export type Handlers = Record<string, KeyboardEventHandler>

const keyMap = {
  ...TOOL_HOTKEYS,
  ...EDGE_DESIGNATOR_HOTKEYS,
  CLEAR_SELECTION: `esc`,
  SELECT_ALL: [`ctrl+a`, `command+a`],
  SNAP_LEFT: `command+left`,
  DELETE_SELECTION: [`del`, `backspace`],
  SHIFT_DOWN: { sequence: `shift`, action: `keydown` },
  SHIFT_UP: { sequence: `shift`, action: `keyup` },
  SPACE_DOWN: { sequence: `space`, action: `keydown` },
  SPACE_UP: { sequence: `space`, action: `keyup` },
  UNDO: [`ctrl+z`, `command+z`],
  REDO: [`ctrl+shift+z`, `command+shift+z`],
  SAVE: [`ctrl+s`, `command+s`],
  ROTATE_PANELS: [`r`],
  SWAP_ANGLES: [`q`],
  ENABLE_DEBUG: `alt+h+alt+j`,
}

const Keybinds_Internal: React.FC<{ design: ReturnType<UseDesign> }> = ({
  design,
}) => {
  const { save } = design
  const nodeIds = useRecoilValue(nodeIndex)
  const setShowDebug = useSetRecoilState(debugPanelIsPresentState)
  const [tool, setTool] = useRecoilState(toolState)
  const setTemporaryTool = useSetRecoilState(temporaryToolState)
  const resetTemporaryTool = useResetRecoilState(temporaryToolState)
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const trySwapToPanelEraser = useTrySwapToPanelEraser()
  const setEdgeDesignation = useSetRecoilState(edgeDesignationState)
  const shiftDown = useToggleState(shiftIsDownState)
  const deleteSelection = useDeleteSelection(nodeIds)
  const selectAll = useSelectAll()
  const clearMeshSelection = useClearSelection()
  const rotatePanels = useSetRecoilState(panelSelectionRotationState)
  const [undo, redo] = useUndoRedo()
  const swapPreviewEdgeAngle = useSwapPreviewEdgeAngle()

  const handlers: Handlers = {
    ...mapObject(TOOL_HOTKEYS, (_, tool) => () => setTool(tool)),
    ...(tool === `EdgeDesignator` &&
      mapObject(
        EDGE_DESIGNATOR_HOTKEYS,
        (_, edgeType) => () => setEdgeDesignation(edgeType),
      )),
    CLEAR_SELECTION: clearMeshSelection,
    SELECT_ALL: (e) => (e?.preventDefault(), selectAll()),
    DELETE_SELECTION: deleteSelection,
    SHIFT_DOWN: () => (shiftDown.on(), trySwapToPanelEraser()),
    SHIFT_UP: () => (shiftDown.off(), trySwapToPanelEraser()),
    SPACE_DOWN: () => setTemporaryTool(`Pan`),
    SPACE_UP: () => resetTemporaryTool(),
    UNDO: (e) => (e?.preventDefault(), undo()),
    REDO: (e) => (e?.preventDefault(), redo()),
    SAVE: (e) => (e?.preventDefault(), save()),
    ROTATE_PANELS: () => rotatePanels({ willReorient90: true }),
    SWAP_ANGLES: () => swapPreviewEdgeAngle(),
    ENABLE_DEBUG: () => setShowDebug((showDebug) => !showDebug),
  }

  return (
    <>
      {currentUserIsDesignUser && (
        <>
          {/* @ts-expect-error hotkeys doesn't know its own types 🙁*/}
          <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
        </>
      )}
    </>
  )
}

export const Keybinds = React.memo(Keybinds_Internal)
